/**
 * Privacy policy, served publicly at `/legal/privacy`.
 *
 * Intuit requires a privacy policy at a public URL before a QuickBooks Online
 * app is published, and it is checked against what the app actually does. So
 * this describes the real system rather than a generic template: the exact
 * entities read from QuickBooks and Clio, the one cookie the app sets, the
 * absence of any analytics or advertising tracker, and the fact that the
 * QuickBooks connection is read-only. If the connector changes, this changes.
 *
 * The honest framing matters as much as the detail. This is an internal
 * business application, not a consumer service — most of the personal
 * information in it belongs to the Firm's clients and staff and arrives from
 * the Firm's own systems, not from a visitor filling in a form.
 */
import { LegalDocument, type LegalSection } from '../components/LegalDocument'
import { LEGAL_CONTACT_EMAIL, LEGAL_EFFECTIVE_DATE, LEGAL_GOVERNING_STATE } from '../lib/legal'

const SECTIONS: readonly LegalSection[] = [
  {
    id: 'scope',
    title: 'What this policy covers',
    body: (
      <>
        <p>
          This policy describes how the Divorce with a Plan Finance Platform (the{' '}
          <strong>Platform</strong>), operated by Divorce with a Plan (the <strong>Firm</strong>),
          handles personal information.
        </p>
        <p>The Platform is an internal application. It is not a consumer service:</p>
        <ul>
          <li>
            There is no public sign-up, no marketing list, and nothing to buy. Accounts exist only
            because an administrator created them for a member of the Firm.
          </li>
          <li>
            Most of the personal information it holds belongs to the Firm&rsquo;s clients and staff
            and arrives from the Firm&rsquo;s own systems — QuickBooks Online and Clio — rather than
            from anyone typing it in here.
          </li>
        </ul>
        <p>
          For that client information the Firm is the controller and this Platform is one of the
          places it is processed. Handling of it is also governed by the Firm&rsquo;s client
          engagement terms and its professional obligations, which take precedence over this policy
          where they are stricter.
        </p>
      </>
    ),
  },
  {
    id: 'what',
    title: 'What the Platform holds',
    body: (
      <>
        <h3>About the people who use it</h3>
        <ul>
          <li>
            Name, Firm email address, and the Google account identifier returned when you sign in.
          </li>
          <li>The role assigned to your account, and whether it is active.</li>
          <li>
            Your notification preferences — which alerts you want, on which channel (in-app, email,
            or Slack), and at what times.
          </li>
          <li>
            A record of what your account did: changes to financial data, requests that were
            refused, and the alerts and digests delivered to you. This audit record cannot be edited
            or deleted from within the Platform.
          </li>
        </ul>

        <h3>Financial and practice records, copied from the Firm&rsquo;s systems</h3>
        <ul>
          <li>
            <strong>From Clio:</strong> clients (display and legal name, email address, telephone
            number), matters, attorneys, time entries, invoices, client payments, trust ledger
            entries, and client refunds.
          </li>
          <li>
            <strong>From QuickBooks Online:</strong> the chart of accounts, invoices, payments,
            vendors, and bills.
          </li>
        </ul>
        <p>
          These are copies. The Platform derives figures from them — receivables, forecasts, trust
          balances, exception queues — and stores those derived figures too.
        </p>

        <h3>Technical and operational records</h3>
        <ul>
          <li>
            Access credentials for the connected systems, held as OAuth tokens encrypted at rest
            with AES-256-GCM under a key separate from the application key. No password for any
            connected system is ever held, because none is ever issued to the Platform.
          </li>
          <li>
            A history of sync runs — when each source was read, how many records, and any error.
          </li>
          <li>
            Ordinary server logs, which include IP addresses, timestamps, and the paths requested.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'sources',
    title: 'Where it comes from',
    body: (
      <ul>
        <li>
          <strong>From you</strong> — your notification preferences, and the filters and dates you
          enter while using the Platform.
        </li>
        <li>
          <strong>From Google</strong> — on sign-in, your name, Firm email address, the domain your
          account belongs to, and a stable account identifier. Nothing else in your Google account
          is read.
        </li>
        <li>
          <strong>From Clio and QuickBooks Online</strong> — on a schedule, and when an
          administrator presses &ldquo;Sync now&rdquo;.
        </li>
        <li>
          <strong>From an administrator</strong> — your account and the role on it, created before
          your first sign-in.
        </li>
      </ul>
    ),
  },
  {
    id: 'why',
    title: 'Why it is held',
    body: (
      <>
        <p>Each category is held for a stated purpose and not repurposed:</p>
        <ul>
          <li>
            <strong>Financial and practice records</strong> — to report the Firm&rsquo;s revenue,
            receivables, trust balances, billing exceptions, and cash flow, and to reconcile the two
            source systems against each other.
          </li>
          <li>
            <strong>Account and role information</strong> — to establish who you are and decide
            which pages and figures you may see.
          </li>
          <li>
            <strong>Audit records</strong> — to answer who changed a financial figure and when, and
            to detect attempts to reach data a role does not hold. This is a professional and
            regulatory requirement for a firm that handles client funds, and it is the one category
            that cannot be switched off.
          </li>
          <li>
            <strong>Notification preferences and delivery records</strong> — to send the alerts you
            asked for, and to avoid sending the same one twice.
          </li>
          <li>
            <strong>Sync history and server logs</strong> — to keep the Platform working, and to
            tell you how stale a figure is.
          </li>
        </ul>
        <p>
          The Platform does not use any of this to build a profile, to make an automated decision
          about any individual, or to train a machine learning model. It is not used for
          advertising, and none of it is sold.
        </p>
      </>
    ),
  },
  {
    id: 'quickbooks',
    title: 'QuickBooks Online specifically',
    body: (
      <>
        <p>
          When an administrator connects the Firm&rsquo;s QuickBooks Online company, the Platform
          requests the QuickBooks Online Accounting scope
          (<code>com.intuit.quickbooks.accounting</code>) and receives an access token and a refresh
          token, both encrypted at rest. What it then does with that access:
        </p>
        <ul>
          <li>
            <strong>Reads only.</strong> The Platform queries the Accounting API and issues no
            create, update, or delete request of any kind. Nothing in the Firm&rsquo;s QuickBooks
            company is altered by this application.
          </li>
          <li>
            <strong>Reads five things.</strong> The chart of accounts, invoices, payments, vendors,
            and bills — on a nightly schedule, incrementally where the API allows it. Payroll data
            is not read, and no scope granting it is requested.
          </li>
          <li>
            <strong>Keeps the copy inside the Firm.</strong> Data read from QuickBooks is stored on
            the Firm&rsquo;s own infrastructure and is not sent to any third party other than the
            infrastructure and notification providers named below.
          </li>
        </ul>
        <p>
          Intuit&rsquo;s handling of the Firm&rsquo;s QuickBooks data is governed by
          Intuit&rsquo;s own privacy statement, not by this policy. An administrator can disconnect
          QuickBooks at any time on the Integrations screen; the tokens are discarded and no further
          reading occurs. Data already synced remains until it is removed under the retention
          practice below.
        </p>
      </>
    ),
  },
  {
    id: 'sharing',
    title: 'Who it is shared with',
    body: (
      <>
        <p>
          The Platform does not sell personal information and does not share it for advertising. It
          is disclosed only to the providers that operate it, and only to the extent each needs:
        </p>
        <ul>
          <li>
            <strong>Amazon Web Services</strong> — hosting and storage of the application and its
            database.
          </li>
          <li>
            <strong>Google</strong> — sign-in, and delivery of alert email through the Firm&rsquo;s
            own Workspace account.
          </li>
          <li>
            <strong>Slack</strong> — delivery of alert messages, where a rule or a person has been
            configured to receive them there.
          </li>
          <li>
            <strong>Intuit and Clio</strong> — the sources being read. Reading from them
            necessarily discloses that a request was made, not the contents of anything else.
          </li>
        </ul>
        <p>
          Alerts and digests carry financial figures, and sometimes a matter or client reference,
          into email and Slack. That is the point of them, and it is a deliberate choice by the
          Firm: what each message may contain is kept to the minimum that makes it useful, and who
          receives it is set per rule and per person.
        </p>
        <p>
          Beyond that, information is disclosed only where the law requires it, or to the
          Firm&rsquo;s professional advisers under a duty of confidence.
        </p>
      </>
    ),
  },
  {
    id: 'cookies',
    title: 'Cookies and tracking',
    body: (
      <>
        <p>
          The Platform sets <strong>one</strong> cookie, <code>jla_session</code>. It carries your
          session so that reloading the page does not sign you out. It is <code>httpOnly</code>, so
          no script can read it; encrypted, so it is opaque to anything holding the cookie jar;{' '}
          <code>SameSite=Lax</code>; and it expires with your session, within twelve hours.
        </p>
        <p>
          There is nothing else. No analytics, no advertising or marketing tags, no session
          recording, no third-party scripts other than Google&rsquo;s own sign-in library, which is
          loaded to render the sign-in button. Because the only cookie is strictly necessary to
          provide the Platform, there is no consent banner to dismiss.
        </p>
      </>
    ),
  },
  {
    id: 'security',
    title: 'How it is protected',
    body: (
      <ul>
        <li>Every request is served over TLS.</li>
        <li>
          Sign-in is by Google, restricted to the Firm&rsquo;s Workspace domain and verified against
          the signed claim rather than a value the browser supplies. The Platform stores no
          passwords.
        </li>
        <li>
          Access is granted per role and checked on the server for every request, including for each
          figure on a consolidated dashboard. A page the client happens to render is not access.
        </li>
        <li>
          Integration tokens are encrypted at rest under a key that can be rotated without ending
          anyone&rsquo;s session.
        </li>
        <li>Changes to financial data are written to an audit log the Platform cannot alter.</li>
        <li>
          Sessions last twelve hours; the browser holds the working token in memory only, never in
          local storage.
        </li>
      </ul>
    ),
  },
  {
    id: 'retention',
    title: 'How long it is kept',
    body: (
      <ul>
        <li>
          <strong>Records synced from QuickBooks and Clio</strong> are kept while they remain
          relevant to the Firm&rsquo;s reporting and its own record-keeping obligations. A record
          deleted in a source system is not automatically deleted here.
        </li>
        <li>
          <strong>Derived snapshots</strong> — executive metrics and billing forecasts — are pruned
          automatically to a bounded window, because their purpose is a trend rather than a
          permanent record.
        </li>
        <li>
          <strong>Audit records</strong> are retained for as long as the Firm&rsquo;s professional
          and regulatory obligations require, and are not deleted on request.
        </li>
        <li>
          <strong>Account records</strong> are kept while your account exists, and the audit trail of
          what the account did outlives it.
        </li>
        <li>
          <strong>Integration tokens</strong> are discarded when a source is disconnected.
        </li>
      </ul>
    ),
  },
  {
    id: 'rights',
    title: 'Your choices, and your rights',
    body: (
      <>
        <p>
          If you hold an account: you can see and change your notification preferences on your
          account screen, and you can ask an administrator to correct your name, email address, or
          role. You cannot switch off audit recording, and neither can an administrator — that is
          deliberate.
        </p>
        <p>
          Depending on where you live, you may have rights to access, correct, or delete personal
          information about you, to obtain a copy of it, and to appeal a refusal — including under{' '}
          {LEGAL_GOVERNING_STATE} law. To exercise any of them, write to{' '}
          <a href={`mailto:${LEGAL_CONTACT_EMAIL}`}>{LEGAL_CONTACT_EMAIL}</a>. The Firm will verify
          who you are before acting, and will say so where a request is refused and why — a
          professional retention obligation or an audit record is the usual reason.
        </p>
        <p>
          If you are a client of the Firm, requests about your information are handled through the
          Firm&rsquo;s normal client channels rather than through this Platform, and are answered
          against the Firm&rsquo;s records as a whole.
        </p>
      </>
    ),
  },
  {
    id: 'children',
    title: 'Children',
    body: (
      <p>
        The Platform is a workplace tool for the Firm&rsquo;s personnel. It is not directed to
        children, and the Firm does not knowingly create accounts for anyone under 18.
      </p>
    ),
  },
  {
    id: 'contact',
    title: 'Changes, and how to reach us',
    body: (
      <>
        <p>
          The Firm may revise this policy. The effective date above changes when it does, and a
          change that materially affects how information is used will be communicated to account
          holders rather than left to be noticed.
        </p>
        <p>
          Questions, requests, and complaints about privacy go to{' '}
          <a href={`mailto:${LEGAL_CONTACT_EMAIL}`}>{LEGAL_CONTACT_EMAIL}</a>. Please write
          &ldquo;Privacy&rdquo; in the subject line so it reaches the right person.
        </p>
      </>
    ),
  },
]

export function Privacy() {
  return (
    <LegalDocument
      title="Privacy Policy"
      effectiveDate={LEGAL_EFFECTIVE_DATE}
      standfirst={
        <p>
          The Divorce with a Plan Finance Platform reads the Firm&rsquo;s own financial and practice
          data from QuickBooks Online and Clio and reports on it in one place. This policy says
          exactly what it holds, where each part came from, who it reaches, and how long it stays —
          and it describes this application specifically rather than software in general.
        </p>
      }
      sections={SECTIONS}
      related={{ to: '/legal/eula', label: 'the End-User License Agreement' }}
    />
  )
}
